// AGC: ProgressBar - Visual progress indicator
// AGC: Uses official Droid Farm design system colors (droid-*)
// AGC: Primary Green: #46b179 (droid-500)
import React from 'react'

interface ProgressBarProps {
  progress: number // AGC: 0-100
  size?: 'sm' | 'md' | 'lg'
  color?: 'primary' | 'success' | 'warning' | 'danger'
  showLabel?: boolean
  className?: string
}

/**
 * AGC: ProgressBar - Animated progress bar component
 * AGC: Uses Droid Farm design system colors (droid-500 for primary)
 */
export const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  size = 'md',
  color = 'primary',
  showLabel = false,
  className = '',
}) => {
  // AGC: Clamp progress between 0 and 100
  const clampedProgress = Math.min(100, Math.max(0, progress))

  // AGC: Size classes
  const sizeClasses = {
    sm: 'h-1',
    md: 'h-2',
    lg: 'h-3',
  }

  // AGC: Color classes - using design system accent colors
  const colorClasses = {
    primary: 'bg-accent-500',   // AGC: Main brand green
    success: 'bg-success',      // AGC: Success green
    warning: 'bg-warning',      // AGC: Warning amber
    danger: 'bg-error',         // AGC: Error red
  }

  return (
    <div className={`w-full ${className}`}>
      {showLabel && (
        <div className="flex justify-between mb-1">
          <span className="text-xs text-gray-500 dark:text-gray-400">Progresso</span>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{clampedProgress}%</span>
        </div>
      )}
      <div className={`w-full bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden ${sizeClasses[size]}`}>
        <div
          className={`h-full rounded-full transition-all duration-500 ease-out ${colorClasses[color]}`}
          style={{ width: `${clampedProgress}%` }}
          role="progressbar"
          aria-valuenow={clampedProgress}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  )
}

export default ProgressBar
